import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, FormGroup, Label, Input, Button, Alert } from 'reactstrap';
import { supabase } from '../../lib/supabase';
import { toast, Toaster } from 'react-hot-toast';
import './AdminLogin.scss';

const AdminLogin = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true); // Start with loading true
    const [checkingSession, setCheckingSession] = useState(true);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [error, setError] = useState('');

    const from = location.state?.from?.pathname || '/admin/dashboard';

    useEffect(() => {
        checkExistingSession();
    }, []);

    const checkExistingSession = async () => {
        try {
            // Get current session
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();

            if (sessionError) {
                console.error('Session error:', sessionError);
                setCheckingSession(false);
                setLoading(false);
                return;
            }

            // If no session exists
            if (!session) {
                setCheckingSession(false);
                setLoading(false);
                return;
            }

            // Check if session is expired
            const currentTime = Math.floor(Date.now() / 1000);
            const expiresAt = session.expires_at;

            if (expiresAt && currentTime >= expiresAt) {
                // Session expired, sign out
                await supabase.auth.signOut();
                setCheckingSession(false);
                setLoading(false);
                toast.info('Your session has expired. Please login again.');
                return;
            }

            // Session exists and is valid, check if user is admin
            const { data: { user } } = await supabase.auth.getUser();

            if (user) {
                const userRole = user.user_metadata?.role || 'user';

                if (userRole === 'admin') {
                    // Valid admin session, redirect to dashboard
                    toast.success('Welcome back!');
                    navigate(from, { replace: true });
                } else {
                    // User is not admin, sign them out
                    await supabase.auth.signOut();
                    setError('Access denied. Admin privileges required.');
                    setCheckingSession(false);
                }
            }
        } catch (err) {
            console.error('Session check error:', err);
            setError('Failed to check session');
        } finally {
            setCheckingSession(false);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleLogin = async (e) => {
        e.preventDefault();

        if (!formData.email || !formData.password) {
            setError('Please enter both email and password');
            return;
        }

        try {
            setLoading(true);
            setError('');

            // Sign in with Supabase
            const { data, error: signInError } = await supabase.auth.signInWithPassword({
                email: formData.email.trim(),
                password: formData.password
            });

            if (signInError) {
                throw signInError;
            }

            if (!data.user) {
                throw new Error('No user data returned');
            }

            // Check session expiration
            if (data.session) {
                const currentTime = Math.floor(Date.now() / 1000);
                const expiresAt = data.session.expires_at;

                if (expiresAt && currentTime >= expiresAt) {
                    throw new Error('Session has expired');
                }
            }

            // Check if user is admin
            const userRole = data.user.user_metadata?.role || 'user';

            if (userRole !== 'admin') {
                await supabase.auth.signOut();
                throw new Error('Access denied. Admin privileges required.');
            }

            toast.success('Login successful!');
            navigate(from, { replace: true });

        } catch (err) {
            console.error('Login error:', err);
            setError(err.message || 'Invalid credentials or insufficient permissions');
            toast.error(err.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleLogin(e);
        }
    };

    // Show loading while checking session
    if (checkingSession) {
        return (
            <Container className="admin-login-container">
                <Row className="justify-content-center align-items-center min-vh-100">
                    <Col md="6" lg="4">
                        <Card className="login-card">
                            <div className="login-header">
                                <div className="login-logo">
                                    <i className="fas fa-shield-alt"></i>
                                </div>
                                <h2>Checking Session</h2>
                                <p>Verifying your authentication status...</p>
                            </div>
                            <div className="session-checking">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">Please wait while we check your session</p>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <>
            <Toaster position="top-right" />

            <Container className="admin-login-container">
                <Row className="justify-content-center align-items-center min-vh-100">
                    <Col md="6" lg="4">
                        <Card className="login-card">
                            <div className="login-header">
                                <div className="login-logo">
                                    <i className="fas fa-shield-alt"></i>
                                </div>
                                <h2>Admin Login</h2>
                                <p>Enter your credentials to access the admin panel</p>
                            </div>

                            {error && (
                                <Alert color="danger" className="login-alert">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    {error}
                                </Alert>
                            )}

                            <Form onSubmit={handleLogin}>
                                <FormGroup>
                                    <Label for="email">Email Address</Label>
                                    <Input
                                        type="email"
                                        name="email"
                                        id="email"
                                        placeholder="admin@example.com"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        onKeyPress={handleKeyPress}
                                        disabled={loading}
                                        required
                                        autoFocus
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Label for="password">Password</Label>
                                    <Input
                                        type="password"
                                        name="password"
                                        id="password"
                                        placeholder="••••••••"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        onKeyPress={handleKeyPress}
                                        disabled={loading}
                                        required
                                    />
                                </FormGroup>

                                <Button
                                    color="primary"
                                    type="submit"
                                    className="login-btn"
                                    disabled={loading}
                                    block
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Signing in...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-sign-in-alt me-2"></i>
                                            Sign In
                                        </>
                                    )}
                                </Button>
                            </Form>

                            <div className="login-footer">
                                <p className="security-note">
                                    <i className="fas fa-lock me-1"></i>
                                    Secure admin access only
                                </p>
                                <div className="login-info">
                                    <small className="text-muted">
                                        <i className="fas fa-info-circle me-1"></i>
                                        You will be automatically redirected if already logged in
                                    </small>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default AdminLogin;